import { reactPlugin, withAITracking } from "../../../../appInsights";
import { Col, Collapse, Row, Select, Spin, Tooltip } from "antd";
import { cloneDeep } from "lodash";
import Icon from "@assets/icon_config";
import "../../care-gaps/measures/measure.scss";
import { useEffect, useState } from "react";
import membersService from "@services/members.service";
import DataNotFound from "@components/noDataFound";
import { NO_DATA_FOUND } from "../../../../constants/constants";
import HealthemSpinner from "@components/healthemSpinner/healthemSpinner";
import { useMeasuresStore } from "@stores/measuresStore";
import { CARE_GAP_STATUS_OPTIONS, HEALTH_PLAN_CATEGORY_TRANSITION_RULES } from "@utils/constants";
import Notes from "@pages/member-details/care-coordination/components/notes/notes";
import memberDetailsService from "@services/memberDetails.service";

const { Panel } = Collapse;

const HealthPlanMeasures = (props: any) => {
  const dataCardId = "41";
  const [activeCardPanel, setActiveCardPanel] = useState<any>(props?.activeKey?.includes(dataCardId) ? ["1"] : []);
  const getHeading = (headerValue: string) => <span>{headerValue}</span>;
  const patientID = membersService?.getCurrentMember?.patient_id;
  const [measures, setMeasures] = useState<any>([]);
  const [medicationMeasures, setMedicationMeasures] = useState<any>([]);
  const [pharmacyMeasures, setPharmacyMeasures] = useState<any>([]);
  const [loading, setLoading] = useState(false);
  const [prevPatientId, setPrevPatientId] = useState(patientID);
  const selectedYear = useMeasuresStore((state) => state.yearSelectedInHealthPlanGaps);
  const setSelectedYear = useMeasuresStore((state) => state.setYearSelectedInHealthPlanGaps);
  const comingFromMeasuresReport = useMeasuresStore((state) => state.comingFromMeasuresReport);
  const reportSelectedYear = useMeasuresStore((state) => state.selectedYear);
  const memberStatus = membersService?.getCurrentMember?.patient_status;
  const isMemberActive = memberStatus === "Active";
  const currentYear = new Date().getFullYear();
  const startYear = 2024;
  const years = Array.from({ length: currentYear - startYear + 1 }, (_, index) => ({
    id: currentYear - index,
    value: (currentYear - index).toString(),
  }));

  const getHealthPlanData = (selectedYear?: any) => {
    setLoading(true);
    memberDetailsService.gethHealthPlanCareGapsInfo(patientID, selectedYear).subscribe({
      next: (res: any) => {
        setMeasures(res.data.data.measuresData);
        setMedicationMeasures(res.data.data.medicationAdherenceMeasuresData);
        setPharmacyMeasures(res.data.data.pharmacyGapsMeasuresData);
        setLoading(false);
      },
      error: (error) => {
        setLoading(false);
      },
    });
  };

  const isTransitionAllowed = (current: string, next: string) => {
    const allowed = HEALTH_PLAN_CATEGORY_TRANSITION_RULES[current] || [];
    return allowed.includes(next);
  };

  const SECTION_STATE_MAP: any= {
    "MEASURE-SECTION": {
      data: measures,
      setData: setMeasures
    },
    "MEDICATION-SECTION": {
      data: medicationMeasures,
      setData: setMedicationMeasures
    },
    "PHARMACY-SECTION": {
      data: pharmacyMeasures,
      setData: setPharmacyMeasures
    }
  };

  const handleStatusChange = (newValue: any, index: any, sectionName: string) => {
    const sectionConfig = SECTION_STATE_MAP[sectionName];
    if (!sectionConfig) return;

    const { data, setData } = sectionConfig;
    const updated = [...data];
    const previousValue = updated[index].category;
    if (!isTransitionAllowed(previousValue, newValue)) return;

    if (newValue === previousValue) return;
    updated[index].category = newValue;
    setData(updated);
    setLoading(true);
    let measureId = updated[index].measureId;
    let previousCategory = previousValue;
    let category = newValue;

    memberDetailsService.updateHealthPlanCategory(patientID, measureId, previousCategory, category, selectedYear, sectionName)
      .subscribe({
        next: (res) => {
          getHealthPlanData(selectedYear);
          setLoading(false);
        },
        error: () => {
          updated[index].category = previousValue;
          setMeasures(updated);
          setLoading(false);
        },
      });
  };

  const renderRecords = () => {
    return measures.map((record: any, index: number) => {
      return (
        <Row key={index} className="measureContainer__record__row">
          <Col span={14} className="f16 measureContainer__record__col__measureName">
            <span>{record.measureName}</span>
            <Tooltip
              overlayStyle={{ maxWidth: "500px" }}
              title={
                record.description && (
                  <>
                    <span className="f16">{record.description}</span>
                    <br />
                    {<span>Domain: {record.domain}</span>}
                  </>
                )
              }
            >
              <span className="pointer measureContainer__record__col__measureName__infoIcon">
                <Icon name="info" colorOfBrand="#7A869A" height={"20"} width={"20"} />
              </span>
            </Tooltip>
          </Col>
          <Col span={5} className="f14 w400 measureContainer__record__col__other">
            <Select
              style={{ width: "100%" }}
              value={record.category}
              onChange={(value) => handleStatusChange(value, index, "MEASURE-SECTION")}
              className="measureContainer__record__col__care-gap-option"
            >
              {CARE_GAP_STATUS_OPTIONS.map((option) => (
                <Select.Option
                  key={option}
                  value={option}
                  disabled={!isTransitionAllowed(record.category, option)}
                >
                  {option}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col span={3} className="f14 w400 measureContainer__record__col__other">{record.hasGap}</Col>
          <Col span={2} className="f14 measureContainer__record__col__other">
            <Notes 
              masterData={record} 
              screen={"HEALTHPLAN-CARE-GAPS"} 
              patientId={patientID}
              measureId={record.measureId}
              category={record.category}
              measureName={record.measureName}
              sectionName="MEASURE-SECTION"
            />
          </Col>
        </Row>
      );
    });
  };

  const renderPharmacyAndMedicationData = (data: any, sectionName: string) => {
    return data.map((record: any, index: any) => (
      <Row key={index} className="measureContainer__record__row">
        <Col span={14} className="f16 measureContainer__record__col__measureName">
          <span>{record.measureName}</span>
          <Tooltip
            overlayStyle={{ maxWidth: "500px" }}
            title={
              record.description && (
                <>
                  <span className="f16">{record.description}</span>
                  <br />
                  {<span>Domain: {record.domain}</span>}
                </>
              )
            }
          >
            <span className="pointer measureContainer__record__col__measureName__infoIcon">
              <Icon name="info" colorOfBrand="#7A869A" height={"20"} width={"20"} />
            </span>
          </Tooltip>
        </Col>
        <Col span={5} className="f14 w400 measureContainer__record__col__other">
          <Select
            style={{ width: "100%" }}
            value={record.category}
            onChange={(value) => handleStatusChange(value, index, sectionName)}
            className="measureContainer__record__col__care-gap-option"
          >
            {CARE_GAP_STATUS_OPTIONS.map((option) => (
              <Select.Option
                key={option}
                value={option}
                disabled={!isTransitionAllowed(record.category, option)}
              >
                {option}
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col span={3} className="f14 w400 measureContainer__record__col__other">
          {record.hasGap}
        </Col>
        <Col span={2} className="f14 measureContainer__record__col__other">
          <Notes
            masterData={record}
            screen={"HEALTHPLAN-CARE-GAPS"}
            patientId={patientID}
            measureId={record.measureId}
            category={record.category}
            measureName={record.measureName}
            sectionName={sectionName}
          />
        </Col>
      </Row>
    ));
  };

  useEffect(() => {
    setActiveCardPanel(props?.activeKey?.includes(dataCardId) ? ["1"] : []);
  }, [props?.activeKey]);

  const onCollapseChange = () => {
    if (activeCardPanel.length > 0) {
      props?.setActivePanels(cloneDeep(props?.activeKey)?.filter((item: any) => item !== dataCardId));
    } else props?.setActivePanels([...(props?.activeKey ? props?.activeKey : []), dataCardId]);
  };

  useEffect(() => {
    if (comingFromMeasuresReport && reportSelectedYear) {
      if (selectedYear !== reportSelectedYear) {
        setSelectedYear(reportSelectedYear);
      }
    } else {
      if (selectedYear !== currentYear.toString()) {
        setSelectedYear(currentYear.toString());
      }
    }
  }, []);

  useEffect(() => {
    if (selectedYear) {
      getHealthPlanData(selectedYear);
    }
  }, [selectedYear]);

  useEffect(() => {
    if (patientID !== prevPatientId) {
      getHealthPlanData(selectedYear);
      setPrevPatientId(patientID);
    }
  }, [patientID]);

  const handleYearChange = (value: string) => {
    setSelectedYear(value);
  };

  const genExtra = () => (
    <Row onClick={(e) => e.stopPropagation()} align={"middle"}>
      <Select size="large" style={{ width: 130 }} value={selectedYear} onChange={handleYearChange} onClick={(e) => e.stopPropagation()} className="mr-1">
        {years.map((year) => (
          <Select.Option key={year.id} value={year.value}>
            PY {year.value}
          </Select.Option>
        ))}
      </Select>
    </Row>
  );

  const renderSubPanels = (title: string, key: string, data: any[], sectionName: string, addSeparator: boolean = false) => (
    <Collapse bordered={false} expandIconPosition="right" className="nested-collapse" accordion>
      <Panel
        header={title}
        key={key}
        className={`f18 w500 ${addSeparator ? "measureContainer__separator" : ""}`}
      >
        <Row className="measureContainer__heading f14">
          <Col span={14}>Measure Name</Col>
          <Col span={5} className="measureContainer__heading__center">
            Category
          </Col>
          <Col span={3} className="measureContainer__heading__center">
            Has Gap?
          </Col>
          <Col span={2} className="measureContainer__heading__center">
            Notes
          </Col>
        </Row>

        {!(medicationMeasures?.length || pharmacyMeasures?.length) ? (
          <DataNotFound message={NO_DATA_FOUND} />
        ) : (
          <div className="measureContainer__record">
            {renderPharmacyAndMedicationData(data, sectionName)}
          </div>
        )}
      </Panel>
    </Collapse>
  );

  return (
    <div className="measureContainer">
      <Spin spinning={loading} indicator={<HealthemSpinner size={50} />}>
        <Collapse accordion={true} activeKey={activeCardPanel} expandIconPosition="right" bordered={false} onChange={onCollapseChange}>
          <Panel header={getHeading("Measures")} key="1" className="f18 w500" extra={genExtra()}>
            {isMemberActive && (
              <Row className="measureContainer__heading f14">
                <Col span={14}>Measure Name</Col>
                <Col span={5} className="measureContainer__heading__center">
                  Category
                </Col>
                <Col span={3} className="measureContainer__heading__center">
                  Has Gap?
                </Col>
                <Col span={2} className="measureContainer__heading__center">
                  Notes
                </Col>
              </Row>
            )}
            {!measures || !measures.length ? (
              <DataNotFound message={NO_DATA_FOUND} />
            ) : (
              <div className="measureContainer__record">{renderRecords()}</div>
            )}
          </Panel>
          {renderSubPanels("Medication Adherence", "2", medicationMeasures, "MEDICATION-SECTION", true)}
          {renderSubPanels("Pharmacy Gap", "3", pharmacyMeasures, "PHARMACY-SECTION")}
        </Collapse>
      </Spin>
    </div>
  );
};

export default withAITracking(reactPlugin, HealthPlanMeasures);
